import { useState } from "react";
import { entryLines } from "@/mocks/home";

const statusStyle: Record<string, { dot: string; text: string; bg: string; ring: string }> = {
  online: {
    dot: "bg-accent-500",
    text: "text-accent-800",
    bg: "bg-accent-100",
    ring: "bg-accent-500/40",
  },
  busy: {
    dot: "bg-secondary-500",
    text: "text-secondary-900",
    bg: "bg-secondary-100",
    ring: "bg-secondary-500/40",
  },
};

export default function EntryStatusBoard() {
  const [copied, setCopied] = useState<string | null>(null);

  const handleCopy = (id: string, code: string) => {
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(code).catch(() => undefined);
    }
    setCopied(id);
    window.setTimeout(() => setCopied(null), 1800);
  };

  return (
    <section id="entries" className="w-full bg-background-50 py-14 md:py-20">
      <div className="w-full px-4 md:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8 md:mb-10">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-2 text-xs font-label tracking-widest text-primary-600 uppercase">
              <i className="ri-radar-line" />
              Entry Monitor
            </span>
            <h2 className="mt-3 font-heading font-black text-3xl md:text-4xl text-foreground-950">
              入口状态板
            </h2>
            <p className="mt-3 text-foreground-600 leading-relaxed">
              四条线路各自独立、互不影响。状态点和延迟每几分钟刷新一次，哪条畅通走哪条，
              这就是黑料入口备用与防屏蔽最实用的一屏。
            </p>
          </div>
          <div className="flex items-center gap-2 px-4 py-3 rounded-md bg-background-100 border border-background-200 shrink-0">
            <span className="relative w-2.5 h-2.5 flex items-center justify-center">
              <span className="hl-status-ring absolute inline-flex w-2.5 h-2.5 rounded-full bg-accent-500/60" />
              <span className="relative inline-flex w-2.5 h-2.5 rounded-full bg-accent-500" />
            </span>
            <span className="text-sm font-label text-foreground-700">4 条线路在线检测中</span>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {entryLines.map((line) => {
            const style = statusStyle[line.status] ?? statusStyle.online;
            return (
              <div
                key={line.id}
                className={`flex flex-col p-5 rounded-lg border transition-colors ${
                  line.highlight
                    ? "bg-foreground-950 border-foreground-950"
                    : "bg-background-50 border-background-200 hover:border-primary-300"
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="relative w-2.5 h-2.5 flex items-center justify-center">
                        <span
                          className={`hl-status-ring absolute inline-flex w-2.5 h-2.5 rounded-full ${style.ring}`}
                        />
                        <span className={`relative inline-flex w-2.5 h-2.5 rounded-full ${style.dot}`} />
                      </span>
                      <h3
                        className={`font-heading font-bold text-lg truncate ${
                          line.highlight ? "text-background-50" : "text-foreground-950"
                        }`}
                      >
                        {line.name}
                      </h3>
                    </div>
                    <p
                      className={`mt-1.5 text-xs font-label ${
                        line.highlight ? "text-background-300" : "text-foreground-500"
                      }`}
                    >
                      {line.region}
                    </p>
                  </div>
                  <span
                    className={`shrink-0 px-2.5 py-1 rounded-md text-xs font-label font-medium ${style.bg} ${style.text}`}
                  >
                    {line.statusText}
                  </span>
                </div>

                <div
                  className={`mt-5 flex items-center justify-between rounded-md px-3 py-2.5 ${
                    line.highlight ? "bg-background-50/10" : "bg-background-100"
                  }`}
                >
                  <span
                    className={`font-mono text-sm truncate ${
                      line.highlight ? "text-background-100" : "text-foreground-800"
                    }`}
                  >
                    {line.code}
                  </span>
                  <span
                    className={`text-xs font-label shrink-0 ${
                      line.highlight ? "text-accent-300" : "text-foreground-500"
                    }`}
                  >
                    {line.latency}
                  </span>
                </div>

                <p
                  className={`mt-3 text-xs leading-relaxed ${
                    line.highlight ? "text-background-300" : "text-foreground-500"
                  }`}
                >
                  {line.note}
                </p>

                <div className="mt-4 flex items-center gap-2">
                  <a
                    href="#top"
                    className={`flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-md text-sm font-label font-medium transition-colors whitespace-nowrap ${
                      line.highlight
                        ? "bg-primary-500 hover:bg-primary-600 text-background-50"
                        : "bg-primary-500 hover:bg-primary-600 text-background-50"
                    }`}
                  >
                    <i className="ri-external-link-line" />
                    进入
                  </a>
                  <button
                    type="button"
                    onClick={() => handleCopy(line.id, line.code)}
                    className={`w-10 h-10 flex items-center justify-center rounded-md border transition-colors ${
                      line.highlight
                        ? "border-background-50/25 text-background-200 hover:bg-background-50/10"
                        : "border-background-300 text-foreground-600 hover:bg-background-100"
                    }`}
                    aria-label="复制入口"
                  >
                    <i className={copied === line.id ? "ri-check-line" : "ri-file-copy-line"} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-6 flex items-start gap-3 p-4 rounded-lg bg-primary-50 border border-primary-100">
          <i className="ri-information-line text-primary-600 text-lg mt-0.5" />
          <p className="text-sm text-foreground-700 leading-relaxed">
            状态板为演示效果，显示的延迟与状态用于呈现工具型排版。收藏本页后，
            每次来都能在这里看到最新一批黑料入口新地址。
          </p>
        </div>
      </div>

      {copied && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[60] flex items-center gap-2 px-5 py-3 rounded-md bg-foreground-950 text-background-50 text-sm font-label">
          <i className="ri-checkbox-circle-line text-accent-400" />
          入口已复制，记得一起存好备用线
        </div>
      )}
    </section>
  );
}